import { timeInTz } from '../../lib/format';
import { timingOf, durationLabel, varianceLabel, type VisitTimes } from './visitTiming';

/**
 * Planned against actual, at the top of the work order (119, ref 23411385).
 *
 * The reference puts two blocks side by side — *Planned 2h:00m:00s · 09:00–12:00* against
 * *Actual 1m:33s · 09:00 – --:--* — and the `--:--` is the interesting part: it draws the
 * missing end as visibly missing rather than as a number.
 *
 * ⭐ **Every honesty rule lives in `./visitTiming`, not here**, because `apps/web` tests in
 * node and this file cannot be tested. What is left here is markup and two `Intl` calls.
 *
 * ⚠️ **This card renders a MISSING duration as "—", never as 0.** The three cases it must
 * not flatten: no booked end (common — neither creation sheet sets one), not yet started,
 * and a variance against a plan that never had a length.
 */
export function PlannedVsActual({ times, tz }: { times: VisitTimes; tz: string }) {
  const t = timingOf(times);
  const variance = varianceLabel(t.varianceMinutes);

  return (
    <section
      className="grid grid-cols-2 gap-px overflow-hidden rounded-[var(--radius)] border border-border bg-border"
      aria-label="Planned against actual time"
    >
      <Half
        label="Planned"
        duration={durationLabel(t.plannedMinutes)}
        from={times.scheduledStartAt}
        to={times.scheduledEndAt}
        tz={tz}
      />
      <Half
        label="Actual"
        duration={
          // ⚠️ "Not started" rather than "—": the absence has a REASON here, and it is the
          // one the tech is about to change by tapping Start.
          t.actualMinutes === null ? 'Not started' : durationLabel(t.actualMinutes)
        }
        from={times.actualStartAt}
        to={times.actualEndAt}
        tz={tz}
        emphasis
        running={t.running}
        note={variance}
      />
    </section>
  );
}

function Half({
  label,
  duration,
  from,
  to,
  tz,
  emphasis,
  running,
  note,
}: {
  label: string;
  duration: string;
  from: Date | string | null;
  to: Date | string | null;
  tz: string;
  emphasis?: boolean;
  running?: boolean;
  note?: string | null;
}) {
  return (
    <div className={`p-3 ${emphasis ? 'bg-surface-2' : 'bg-surface'}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        <span className="font-heading text-sm font-bold tabular-nums">{duration}</span>
      </div>
      <p className="mt-1 text-xs tabular-nums text-muted-foreground">
        {/* `--:--` for a missing end, exactly as the reference draws it — a blank would read
            as a rendering bug, and a copy of the start time would be a lie. */}
        {from ? timeInTz(from, tz) : '--:--'} – {to ? timeInTz(to, tz) : '--:--'}
      </p>
      {running ? (
        <p className="mt-1 flex items-center gap-1.5 text-xs text-[var(--sw-brand-text)]">
          <span aria-hidden className="inline-block size-1.5 rounded-full bg-current" />
          Running
        </p>
      ) : note ? (
        // Only once BOTH durations are real. `varianceLabel` returns null otherwise, and a
        // variance against a plan with no length is 113's utilization-% mistake again.
        <p className="mt-1 text-xs text-muted-foreground">{note}</p>
      ) : null}
    </div>
  );
}
